import { PointerEvent as ReactPointerEvent } from 'react';

import { Camera, CanvasElement, Point } from '@/features/canvas/types';
import { connectorGeometry, worldToScreen } from './geometry';

const BLUE = '#3b82f6';

/**
 * Screen-space handles for a selected connector (Miro-style): draggable hollow-circle endpoints
 * (drag onto another shape to re-attach), draggable hollow-circle waypoints, and solid segment
 * midpoint dots. Dragging a solid dot on an elbow moves that segment perpendicularly; on a
 * straight/curved connector it drops a bend at the cursor. Constant size at any zoom.
 */
export default function ConnectorHandles({
  cam,
  connector,
  from,
  to,
  onEndpointDown,
  onWaypointDown,
  onSegmentDown,
}: {
  cam: Camera;
  connector: CanvasElement;
  from: CanvasElement | undefined;
  to: CanvasElement | undefined;
  onEndpointDown: (e: ReactPointerEvent, which: 'from' | 'to') => void;
  onWaypointDown: (e: ReactPointerEvent, index: number) => void;
  onSegmentDown: (e: ReactPointerEvent, controlIndex: number, orientation: 'h' | 'v' | 'free' | 'railh' | 'railv', mid: Point) => void;
}) {
  if (!from || !to) return null;
  const g = connectorGeometry(from, to, connector.style ?? {});
  const S = (p: Point) => worldToScreen(cam, p.x, p.y);

  return (
    <div className="pointer-events-none absolute inset-0 z-20">
      {/* endpoints — drag onto another shape to re-attach */}
      {([['from', g.start] as const, ['to', g.end] as const]).map(([which, p]) => {
        const s = S(p);
        return (
          <div
            key={which}
            onPointerDown={(e) => onEndpointDown(e, which)}
            title="Drag to re-attach"
            className="pointer-events-auto absolute rounded-full border-2 bg-white shadow-sm transition-transform hover:scale-125"
            style={{ left: s.x - 6, top: s.y - 6, width: 12, height: 12, borderColor: BLUE, cursor: 'grab' }}
          />
        );
      })}
      {/* existing waypoints — draggable */}
      {g.waypoints.map((wp, i) => {
        const s = S(wp);
        return (
          <div
            key={`wp-${i}`}
            onPointerDown={(e) => onWaypointDown(e, i)}
            className="pointer-events-auto absolute rounded-full border-2 bg-white shadow-sm transition-transform hover:scale-125"
            style={{ left: s.x - 6, top: s.y - 6, width: 12, height: 12, borderColor: BLUE, cursor: 'grab' }}
          />
        );
      })}
      {/* segment midpoints — drag to move the segment / add a bend */}
      {g.handles.map((h, i) => {
        const s = S(h.mid);
        return (
          <div
            key={`seg-${i}`}
            onPointerDown={(e) => onSegmentDown(e, h.controlIndex, h.orientation, h.mid)}
            title="Drag to reshape"
            className="pointer-events-auto absolute rounded-full transition-transform hover:scale-150"
            style={{
              left: s.x - 4,
              top: s.y - 4,
              width: 8,
              height: 8,
              background: BLUE,
              opacity: 0.85,
              cursor:
                h.orientation === 'v' || h.orientation === 'railv'
                  ? 'ew-resize'
                  : h.orientation === 'h' || h.orientation === 'railh'
                    ? 'ns-resize'
                    : 'move',
            }}
          />
        );
      })}
    </div>
  );
}
